// Phase 3B.4.a — common frontend action context.
// Pure, lightweight, no network, no storage, no side effects.
// Bridges an Opportunity (from opportunityEngine) to the action modules (Markets/Campaigns/Partners)
// and is forward-compatible with Lodge AI. It carries context ONLY; the backend remains the sole
// authority for tenant isolation. `organizationId` here is INFORMATIONAL for the frontend only and
// must never be treated as an authorization source.

import { suggestOppKpi } from "@/lib/opportunityEngine";

// The Action Center must describe the opportunity it will actually open. In a
// destination-wide view, the route picker still holds its previous selection;
// callers must therefore derive the label from the ranked opportunity itself.
export function actionMarketLabel(opp) {
  return opp?.market || null;
}

export function buildActionContext(opp, {
  org = {},
  sourceModule = "dashboard-opportunity",
  period = "",
  dataUsed = [],
} = {}) {
  if (!opp) return null;
  const provenance = opp.source || opp.provenance || null;
  const isDemo = opp.type === "demo" || provenance === "Demo";
  const opportunityData = Array.isArray(opp.dataUsed)
    ? opp.dataUsed
    : (Array.isArray(opp.data_used) ? opp.data_used : []);
  const fallbackData = Array.isArray(dataUsed) ? dataUsed : [];
  const resolvedDataUsed = opportunityData.length
    ? opportunityData
    : (fallbackData.length ? fallbackData : (provenance ? [provenance] : []));
  return {
    organizationId: org.id || null, // informational only
    sourceModule,
    opportunityId: opp.id || null,
    market: opp.market || null,
    marketKey: opp.marketKey || opp.market_key || null,
    signal: opp.signal || null,
    evidence: opp.evidence || null,
    recommendedAction: opp.recommendedAction || opp.recommended_action || null,
    objective: opp.opportunity || null,
    period: opp.period || period || "",
    provenance,
    potential: opp.potential || null,
    confidence: opp.confidence || null,
    dataUsed: resolvedDataUsed,
    isDemo,
    suggestedKpis: isDemo ? [] : [{ name: suggestOppKpi(opp.type), target: 0, current: 0 }],
    geography: { level: org.geographic_level || null, entity: org.geographic_entity || null },
  };
}

// Only the fields the existing CampaignForm actually consumes. Nothing is coerced into a field whose
// business meaning doesn't match (e.g. signal/recommendedAction are NOT injected into target_audience).
export function campaignPrefillFrom(ctx) {
  if (!ctx) return null;
  return {
    objective: ctx.recommendedAction || ctx.objective || "",
    market: ctx.market || "",
    period: ctx.period || "",
    kpis: ctx.suggestedKpis || [],
    decision_context: {
      source_module: ctx.sourceModule || "dashboard-opportunity",
      opportunity_id: ctx.opportunityId || null,
      market_key: ctx.marketKey || null,
      signal: ctx.signal || null,
      evidence: ctx.evidence || null,
      recommended_action: ctx.recommendedAction || null,
      provenance: ctx.provenance || null,
      potential: ctx.potential || null,
      confidence: ctx.confidence || null,
      data_used: ctx.dataUsed || [],
      is_demo: !!ctx.isDemo,
    },
  };
}
